import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faWilson } from '@awesome.me/kit-d87edcad8c/icons/kit/custom';
import { IonButton, ModalController } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';

export type OutdatedDeviceComponent = 'os' | 'webView';

export const ANDROID_WEBVIEW_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.google.android.webview';

@Component({
  selector: 'wilson-device-update',
  template: `
    <div class="device-update-wrapper">
      <fa-icon [icon]="faWilson" size="5x"></fa-icon>

      @if (outdatedComponent === 'os') {
        <h1>{{ 'deviceUpdate.os.header' | translate }}</h1>
        <p>{{ 'deviceUpdate.os.message' | translate }}</p>
        <ion-button
          expand="block"
          (click)="openSoftwareUpdateSettings()"
          data-testid="device-update-open-settings"
        >
          {{ 'deviceUpdate.os.button' | translate }}
        </ion-button>
      } @else {
        <h1>{{ 'deviceUpdate.webView.header' | translate }}</h1>
        <p>{{ 'deviceUpdate.webView.message' | translate }}</p>
        <a
          [href]="webViewStoreUrl"
          data-testid="device-update-webview-store-link"
        >
          <ion-button expand="block">
            {{ 'deviceUpdate.webView.button' | translate }}
          </ion-button>
        </a>
      }

      <ion-button
        fill="clear"
        color="dark"
        expand="block"
        (click)="dismiss()"
        data-testid="device-update-dismiss"
      >
        {{ 'deviceUpdate.dismiss' | translate }}
      </ion-button>
    </div>
  `,
  styles: `
    .device-update-wrapper {
      height: 100%;
      padding: 1.5rem;
      gap: 1rem;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
    }

    a {
      width: 100%;
      text-decoration: none;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FontAwesomeModule, IonButton, TranslatePipe],
})
export class DeviceUpdateComponent {
  private readonly modalCtrl = inject(ModalController);

  // Not a signal input(): Ionic 8's ModalController assigns componentProps
  // directly to the instance, which would overwrite the signal.
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() outdatedComponent: OutdatedDeviceComponent = 'os';

  protected readonly faWilson = faWilson;
  protected readonly webViewStoreUrl = ANDROID_WEBVIEW_STORE_URL;

  openSoftwareUpdateSettings() {
    NativeSettings.open({
      optionAndroid: AndroidSettings.Settings,
      optionIOS: IOSSettings.SoftwareUpdate,
    });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
